import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";

import { Layout, Menu, message } from "antd";
import { useEffect, useRef, useState } from "react";

import DowntimeModal from "@/components/DowntimeModal";
import styles from "@/components/PrivateLayout/PrivateLayout.module.css";
import { AlertMessageMoveSpeed, IMAGES, ROUTES_URL } from "@/constant";
import { checkDownTime, showAlert } from "@/services/Auth/AuthApi";
import { useAuthStore } from "@/stores/authStore";
import { AlertItemInterface } from "@/types/authService";

const { Sider, Content } = Layout;

interface MenuChild {
  key: string;
  label: string;
}

interface MenuItemData {
  key: string;
  label: string;
  icon?: {
    active: string;
    inactive: string;
    hover?: string;
  };
  children?: MenuChild[];
}

const PrivateLayout: React.FC = () => {
  const name = useAuthStore((store) => store?.customer?.name);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isTCAccepted = useAuthStore((state) => state.isTCAccepted);
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [messages, setMessages] = useState<string[]>([]);

  const marqueeContainerRef = useRef<HTMLDivElement | null>(null);
  const marqueeTextRef = useRef<HTMLSpanElement | null>(null);

  const animationFrameRef = useRef<number | null>(null);
  const currentMessageIndexRef = useRef<number>(0);
  const messageIndexRef = useRef<number>(0);

  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;

  const releaseTag = process.env.GIT_RELEASE_TAG || "";

  useEffect(() => {
    void getAlert();
  }, []);

  const getAlert = async () => {
    try {
      const response = await showAlert();
      if (response?.data) {
        const extracted = response.data.map((item: AlertItemInterface) => item.alert_message);
        setMessages(extracted);
      }
    } catch (error) {
      console.error("Error", error);
    }
  };

  useEffect(() => {
    if (!messages.length) return;

    startMarquee();

    return () => stopMarquee();
  }, [messages]);

  const startMarquee = () => {
    stopMarquee();

    if (!marqueeTextRef.current || !marqueeContainerRef.current) return;

    const step = () => {
      const textEl = marqueeTextRef.current!;
      const containerEl = marqueeContainerRef.current!;

      const textWidth = textEl.offsetWidth;
      const containerWidth = containerEl.offsetWidth;

      currentMessageIndexRef.current -= AlertMessageMoveSpeed;

      if (currentMessageIndexRef.current < -textWidth - 50) {
        messageIndexRef.current = (messageIndexRef.current + 1) % messages.length;
        textEl.textContent = messages[messageIndexRef.current];

        currentMessageIndexRef.current = containerWidth + 50;
      }

      textEl.style.transform = `translateX(${currentMessageIndexRef.current}px)`;
      animationFrameRef.current = requestAnimationFrame(step);
    };

    animationFrameRef.current = requestAnimationFrame(step);
  };

  useEffect(() => {
    const checkServerDowntime = async (): Promise<void> => {
      try {
        const response = await checkDownTime();

        if (response?.is_downtime) {
          useAuthStore.getState().logout();

          globalThis.location.replace(
            `${ROUTES_URL.LOGIN}?downtime=true` +
              `&start_time=${encodeURIComponent(response.downtime_start_time)}` +
              `&end_time=${encodeURIComponent(response.downtime_end_time)}`,
          );
        }
      } catch (error) {
        console.error("Downtime check failed:", error);
      }
    };

    void checkServerDowntime();

    const intervalId = window.setInterval(() => {
      void checkServerDowntime();
    }, 60 * 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  const stopMarquee = () => {
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    animationFrameRef.current = null;
  };

  const menuItems: MenuItemData[] = [
    {
      key: ROUTES_URL.DASHBOARD,
      label: "Dashboard",
      icon: {
        active: IMAGES.DASHBOARD_ICON,
        inactive: IMAGES.DASHBOARD_INACTIVE_ICON,
        hover: IMAGES.DASHBOARD_HOVER_ICON,
      },
    },
    {
      key: ROUTES_URL.PLEDGE,
      label: "Pledge",
      icon: {
        active: IMAGES.PLEDGE_ICON,
        inactive: IMAGES.PLEDGE_INACTIVE_ICON,
        hover: IMAGES.PLEDGE_HOVER_ICON,
      },
    },
    {
      key: ROUTES_URL.TRANSACTIONS,
      label: "Transactions",
      icon: {
        active: IMAGES.TRANSACTIONS_ICON,
        inactive: IMAGES.TRANSACTIONS_INACTIVE_ICON,
        hover: IMAGES.TRANSACTIONS_HOVER_ICON,
      },
    },
    {
      key: ROUTES_URL.SETTINGS,
      label: "Settings",
      icon: {
        active: IMAGES.SETTINGS_ICON,
        inactive: IMAGES.SETTINGS_INACTIVE_ICON,
        hover: IMAGES.SETTINGS_HOVER_ICON,
      },
    },
    {
      key: ROUTES_URL.NOTIFICATIONS,
      label: "Notifications",
      icon: {
        active: IMAGES.NOTIFICATIONS_ICON,
        inactive: IMAGES.NOTIFICATIONS_INACTIVE_ICON,
        hover: IMAGES.NOTIFICATIONS_HOVER_ICON,
      },
    },
    {
      key: ROUTES_URL.LOGOUT,
      label: "Logout",
      icon: {
        active: IMAGES.DASHBOARD_ICON,
        inactive: IMAGES.LOGOUT_INACTIVE_ICON,
        hover: IMAGES.LOGOUT_HOVER_ICON,
      },
    },
  ];

  const handleOpenChange = (keys: string[]): void => setOpenKeys(keys);

  const menuItemsWithImages = menuItems.map((item) => {
    const isActive = currentPath.startsWith(item.key);
    const isHovered = hoveredKey === item.key;
    const isExpanded = openKeys.includes(item.key);

    let iconSrc = item.icon?.inactive;
    if (isActive || isExpanded) iconSrc = item.icon?.active;
    else if (isHovered) iconSrc = item.icon?.hover;

    return {
      key: item.key,
      label:
        item.key === ROUTES_URL.LOGOUT ? (
          item.label
        ) : (
          <span data-dd-privacy="allow">{item.label}</span>
        ),
      icon: item.icon ? (
        <img src={iconSrc} alt={`${item.label} icon`} className={styles.menuItem} />
      ) : null,
      onMouseEnter: (): void => setHoveredKey(item.key),
      onMouseLeave: (): void => setHoveredKey(null),
      children: item.children?.map((child) => ({ key: child.key, label: child.label })),
    };
  });

  const menuWithDivider = [
    menuItemsWithImages[0],
    { type: "divider" as const, className: "menu-divider" },
    ...menuItemsWithImages.slice(1),
  ];

  const subMenu = menuItemsWithImages.find((item) => currentPath.startsWith(item.key));

  if (!isAuthenticated) return <Navigate to={ROUTES_URL.LOGIN} replace />;

  const logout = () => {
    useAuthStore.getState().logout();
    navigate(ROUTES_URL.LOGIN, { replace: true });
  };

  return isTCAccepted ? (
    <Layout className={styles.layoutRoot}>
      {messages.length ? (
        <div className={styles.alertContainer}>
          <div ref={marqueeContainerRef} className={styles.alertDiv}>
            <span ref={marqueeTextRef} className={styles.alertText} />
          </div>
        </div>
      ) : null}
      <Layout className={`${styles.bodyLayout} ${messages.length > 0 ? styles.topHeight : ""}`}>
        <Sider collapsed={false} trigger={null} width={250} className={styles.sider}>
          <img src={IMAGES.LOGO} className={styles.appTitle} alt="Logo" />
          <Menu
            mode="inline"
            selectedKeys={[location.pathname, subMenu ? subMenu.key : ""]}
            onClick={({ key }) => {
              if (key === ROUTES_URL.LOGOUT) {
                logout();
              } else {
                navigate(key);
              }
            }}
            items={menuWithDivider}
            className={`${styles.menu} ${styles.expanedMenu}`}
            onOpenChange={handleOpenChange}
          />
          <div className={styles.profileWrapper}>
            <img src={IMAGES.PROFILE_ICON} alt="Profile" className={styles.profileImage} />
            <span className={styles.profileName}>{name}</span>
          </div>
        </Sider>
        <Layout>
          <Layout className={styles.innerLayout}>
            <Content className={styles.content}>
              <Outlet />
            </Content>
          </Layout>

          {releaseTag ? (
            <Layout.Footer className={styles.footer}>
              <div className={styles.footerText}>{releaseTag}</div>
            </Layout.Footer>
          ) : null}
        </Layout>
      </Layout>
    </Layout>
  ) : (
    <Navigate to={ROUTES_URL.TERMS} replace />
  );
};

export default PrivateLayout;
